import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    HostListener,
    ViewChild,
    ElementRef,
    AfterViewChecked,
} from '@angular/core';
import {
    BreakpointObserver,
    Breakpoints,
    BreakpointState,
    MediaMatcher,
} from '@angular/cdk/layout';
import { Observable, tap } from 'rxjs';
import { UiService } from '../../../shared/services/ui.service';
import { DestroyRxComponent } from '../../destroy-rx/destroy-rx.component';
import { AuthService } from '../../../shared/services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { UserAssignmentsModalComponent } from '../../../modules/assignments/user-assignments-modal/user-assignments-modal.component';
import { BillSearchModalComponent } from '../../../modules/bills/bill-search-modal/bill-search-modal.component';
import { AppConfigService } from '../../../app.config';
import { Session } from '../../../shared/types/session';
import { UserSessionService } from '../../../shared/services/user-session.service';
import { takeUntil } from 'rxjs/operators';
import { UserSettingsModalComponent } from '../../user-settings-modal/user-settings-modal.component';
import { Router } from '@angular/router';

@Component({
    selector: 'cagov-nav-megamenu',
    templateUrl: './nav-megamenu.component.html',
    styleUrls: ['./nav-megamenu.component.scss'],
    standalone: false,
})
export class CaGovNavMegamenuComponent
    extends DestroyRxComponent
    implements OnInit, OnChanges, OnDestroy, AfterViewChecked
{
    @ViewChild('billSearch') billSearchInput!: ElementRef<HTMLInputElement>;

    @Input() appName: string = 'New App';
    @Input() shortName: string = '';
    @Input() appEnvironment: string = '';
    @Input() appVersion: string = '';
    @Input() logoutMethod: any;

    mobileQuery: MediaQueryList;
    private _mobileQueryListener: () => void;

    mediumQuery: MediaQueryList;
    private _mediumQueryListener: () => void;

    @Input() compact: boolean = false;
    @Input() navItems: NavItem[] | undefined;
    @Input() showSearchControl: boolean = true;
    @Output() search: EventEmitter<any> = new EventEmitter<any>();
    @Output() loginRequested: EventEmitter<any> = new EventEmitter<any>();
    @Output() navigationEvent: EventEmitter<NavEvent> =
        new EventEmitter<NavEvent>();
    @Output() onMainContent: EventEmitter<MouseEvent> =
        new EventEmitter<MouseEvent>();

    @HostListener('document:keydown.escape', ['$event'])
    onEscape(event: KeyboardEvent) {
        if (this.searchOpen) {
            this.closeSearch();
        }
    }

    navOpen: boolean = false;
    searchOpen: boolean = false;
    private previousSearchOpen = false;
    user: any = null;
    bpState: BreakpointState = { matches: false, breakpoints: {} };
    Breakpoints: any = Breakpoints;
    loginDisplay = false;
    authed$: Observable<boolean>;
    currentSession?: Session = undefined;
    skipLinkPath = () => this.uiService.getSkipLink() + '#main-content';

    searchText: string = '';

    constructor(
        private lasAuth: AuthService,
        private changeDetectorRef: ChangeDetectorRef,
        public breakpointObserver: BreakpointObserver,
        public uiService: UiService,
        media: MediaMatcher,
        private matDialog: MatDialog,
        configs: AppConfigService,
        private session: UserSessionService,
        private router: Router,
    ) {
        super();

        this.authed$ = this.lasAuth.authed$.pipe(
            tap((isFullyAuthed) => {
                this.setLoginDisplay(isFullyAuthed);
            }),
            takeUntil(this.destroy$),
        );

        const config = configs.getConfig();
        this.appName = config.title;
        this.shortName = config.shortName;
        this.appVersion = config.version;

        this.mobileQuery = media.matchMedia('(max-width: 767px)');
        this._mobileQueryListener = () =>
            this.changeDetectorRef.detectChanges();
        this.mobileQuery.addEventListener('change', this._mobileQueryListener);

        this.mediumQuery = media.matchMedia('(max-width: 992px)');
        this._mediumQueryListener = () =>
            this.changeDetectorRef.detectChanges();
        this.mediumQuery.addEventListener('change', this._mediumQueryListener);
    }

    /**
     * Returns the current component route to be used in same page navigations.
     */
    get currentRoute() {
        return this.router.url;
    }

    /**
     * @see AuthService.isAdmin
     */
    get isAdmin() {
        return this.lasAuth.isAdmin();
    }

    /**
     * Sets up the menu and breakpoint observables and sets the app name.
     */
    ngOnInit() {
        this.session.session$
            .pipe(takeUntil(this.destroy$))
            .subscribe((session) => {
                this.currentSession = session;
            });

        this.uiService.appMenuOpen
            .pipe(takeUntil(this.destroy$))
            .subscribe((isOpen) => {
                this.navOpen = isOpen;
                this.navOpen ? this.closeSearch() : this.closeNavItems();
            });

        this.breakpointObserver
            .observe([Breakpoints.Small, Breakpoints.XSmall])
            .pipe(takeUntil(this.destroy$))
            .subscribe((state: BreakpointState) => {
                this.bpState = state;
            });

        // If no shortName is set, try to generate one by creating an acronym.
        if (this.shortName == '') {
            const matches = this.appName.match(/\b(\w)/g);
            this.shortName = matches?.join('') ?? 'LAS';
        }
    }

    /**
     * Reloads the user's active msal/AD account.
     */
    ngOnChanges() {
        this.user = this.lasAuth.activeAccount;
    }

    /**
     * Handles search input focus when searchOpen state changes.
     */
    ngAfterViewChecked() {
        // Only focus if searchOpen changed from false to true
        if (
            this.searchOpen &&
            !this.previousSearchOpen &&
            this.billSearchInput
        ) {
            this.billSearchInput.nativeElement.focus();
        }
        this.previousSearchOpen = this.searchOpen;
    }

    /**
     * Shows the login option and hides the navbar if the user isn't fully authed.
     * Shows the logout option and navbar, otherwise.
     * @param isFullyAuthed The authed flag emitted by the auth service.
     */
    setLoginDisplay(isFullyAuthed: boolean) {
        this.loginDisplay = isFullyAuthed;
        this.user = this.lasAuth.activeAccount;
        if (this.loginDisplay) {
            console.log('activeAccount', this.user);
        }
    }

    /**
     * @see AuthService.inRole
     * @param roles A list of allowable roles.
     */
    inRole = (roles: string[]) => this.lasAuth.inRole(roles);

    /**
     * Opens the user assignments modal.
     */
    openUserAssignments(): void {
        this.matDialog.open(UserAssignmentsModalComponent, {
            id: 'user-assignments-dialog',
            ariaLabel: 'user assignments',
            closeOnNavigation: true,
            width: '80%',
            minHeight: '60vh',
            maxHeight: '90vh',
            ariaLabelledBy: 'user-assignments-modal-title',
        });
    }

    /**
     * Opens the user settings modal.
     */
    openSettings = () => this.matDialog.open(UserSettingsModalComponent);

    /**
     * Opens the search results in a modal.
     */
    openSearchResultsDialog(value: string = ''): void {
        // Currently passing click(this) to get a reference to the component.
        // Definitely wrong, but it works for now. Probably going to need a
        // component specifically for sub links.
        this.closeNavItems();
        this.closeSearch();

        this.matDialog.open(BillSearchModalComponent, {
            closeOnNavigation: true,
            data: {
                session: '',
                text: value,
            },
            width: '80%',
        });
    }

    /**
     * Toggles the visibility of the mobile menu.
     */
    toggleMobileControls(): void {
        this.uiService.setAppMenu(!this.navOpen);
    }

    /**
     * Toggles the visibility of the search bar under the navbar.
     * @param $event Emitted search icon mouse click.
     */
    toggleSearch($event: MouseEvent): void {
        $event.preventDefault();
        this.searchOpen = !this.searchOpen;
        if (this.searchOpen) {
            this.navOpen = false;
            this.closeNavItems();
        }
    }

    /**
     * Toggles the visibility of a nav items second level items.
     * @param item The clicked nav item.
     */
    openNavItem(item: NavItem): void {
        let current = item.open;
        this.closeNavItems();
        item.open = !current;
    }

    /**
     * Closes all open nav items.
     */
    closeNavItems(): void {
        this.navItems?.forEach((item) => (item.open = false));
    }

    /**
     * Click handler for the search bar's close button.
     */
    closeSearch(): void {
        this.searchOpen = false;
    }

    /**
     * Triggers the user login flow.
     */
    login = () => this.lasAuth.login();

    /**
     * Triggers the user logout flow.
     */
    logout = () => this.lasAuth.logout();

    /**
     * Handles a click of the `Skip to Main Content` link by delegating the call to the app component's logic.
     * @param event The click event.
     */
    skipToMainContent(event: MouseEvent) {
        this.onMainContent.emit(event);
    }
}

export class NavItem {
    title: string = '';
    icon?: string;
    image?: string;
    description?: string;
    id: string = '';
    open: boolean = false;
    routerLink?: any;
    url?: string;
    click?: any;
    guard?: boolean = false;
    roles: string[] = [];
    subItems?: NavItem[];
    borderItems?: NavItem[];
}

export class NavEvent {
    action: string = '';
    value?: string = '';
}
